import { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { MspLogoImage } from '@/components/msp/MspLogoImage';

interface MSP {
  id: string;
  naam: string;
  plaats: string;
  provincie: string;
  slug: string;
  logo_url?: string | null;
}

interface MSPSearchBoxProps {
  msps: MSP[];
  /**
   * 'floating' keeps the original card that overlaps the section above it.
   * 'embedded' renders the plain panel for use inside the hero grid.
   */
  variant?: 'floating' | 'embedded';
  /** True while the MSP list is still being fetched (shows a loading row instead of an empty dropdown). */
  loading?: boolean;
  /** Called on focus/typing so the owner can start the MSP list query on demand. */
  onActivate?: () => void;
}

export function MSPSearchBox({ msps, variant = 'floating', loading = false, onActivate }: MSPSearchBoxProps) {


  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter MSPs for autocomplete
  const filteredMSPs = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return msps
      .filter(msp => 
        msp.naam.toLowerCase().includes(query) ||
        msp.plaats.toLowerCase().includes(query) ||
        msp.provincie.toLowerCase().includes(query)
      )
      .slice(0, 5);
  }, [msps, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filteredMSPs.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && filteredMSPs[selectedIndex]) {
        navigate(`/msp/${filteredMSPs[selectedIndex].slug}`);
      } else {
        navigate(`/vergelijken?search=${encodeURIComponent(searchQuery)}`);
      }
      setShowDropdown(false);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleSearchChange = (value: string) => {
    onActivate?.();
    setSearchQuery(value);
    setShowDropdown(value.length > 0);
    setSelectedIndex(-1);
  };

  const embedded = variant === 'embedded';

  const searchCard = (
    <div className="bg-card rounded-xl p-4 sm:p-5 border border-border shadow-sm">
      {embedded && (
        <p className="mb-3 text-sm font-semibold text-foreground">
          Zoek direct een MSP
        </p>
      )}
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <Search className="absolute left-3 sm:left-4 top-6 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground z-10 sm:top-7" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Zoek MSP op naam of plaats..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => {
              onActivate?.();
              if (searchQuery.length > 0) setShowDropdown(true);
            }}
            onKeyDown={handleSearchKeyDown}
            className="pl-10 sm:pl-12 h-12 sm:h-14 text-base sm:text-lg rounded-lg pr-3 sm:pr-28"
          />
        </div>
        <Button
          variant="default"
          className="mt-2 h-11 w-full text-sm sm:mt-0 sm:absolute sm:right-2 sm:top-7 sm:h-9 sm:w-auto sm:-translate-y-1/2 sm:px-5 sm:text-base"
          size="sm"
          onClick={() => navigate(`/vergelijken?search=${encodeURIComponent(searchQuery)}`)}
        >
          Zoeken
        </Button>

        {/* Loading row — prevents a silently empty suggestion list while the MSP list loads */}
        {showDropdown && loading && filteredMSPs.length === 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
              <Search className="h-4 w-4" />
              Suggesties laden...
            </div>
          </div>
        )}

        {/* Autocomplete Dropdown */}
        {showDropdown && filteredMSPs.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden">
            {filteredMSPs.map((msp, index) => (
              <Link
                key={msp.id}
                to={`/msp/${msp.slug}`}
                onClick={() => setShowDropdown(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors",
                  index === selectedIndex && "bg-muted"
                )}
              >
                <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center overflow-hidden shrink-0">
                  <MspLogoImage
                    logoUrl={msp.logo_url}
                    variant="card"
                    alt={msp.naam}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    width={40}
                    height={40}
                    fallback={
                      <span className="text-lg font-bold text-muted-foreground">
                        {msp.naam.charAt(0)}
                      </span>
                    }
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground truncate">{msp.naam}</p>
                  <p className="text-sm text-muted-foreground">{msp.plaats}, {msp.provincie}</p>
                </div>
              </Link>
            ))}
            <Link
              to={`/vergelijken?search=${encodeURIComponent(searchQuery)}`}
              onClick={() => setShowDropdown(false)}
              className="flex items-center gap-2 px-4 py-3 bg-muted/50 text-sm text-primary hover:bg-muted transition-colors border-t border-border"
            >
              <Search className="h-4 w-4" />
              Alle resultaten voor "{searchQuery}"
            </Link>
          </div>
        )}
      </div>
    </div>
  );

  if (embedded) {
    return searchCard;
  }

  return (
    <section className="relative z-10 container mx-auto px-4 -mt-6 sm:-mt-8">
      <div className="max-w-2xl mx-auto">{searchCard}</div>
    </section>
  );
}
